import React, { useState, useEffect, useCallback } from 'react';
import { Drawer, Table, Input, Button, Tag, Spin } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { debounce } from 'lodash';
import { StoreStatus } from '../../types/outofstocktypes';

const { Search } = Input;

interface Props {
    visible: boolean;
    onClose: () => void;
    onOk: (selectedStoreGuids: string[], action: 'out' | 'restock') => void;
    itemName: string;
    stores: StoreStatus[];
    loading?: boolean;
    action: 'out' | 'restock';   // NEW prop
}

const StoreSelectorDrawer: React.FC<Props> = ({
    visible,
    onClose,
    onOk,
    itemName,
    stores,
    loading,
    action,
}) => {
    const [searchText, setSearchText] = useState('');
    const [filteredStores, setFilteredStores] = useState<StoreStatus[]>(stores);
    const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    // Debounced search
    const debouncedSearch = useCallback(
        debounce((value: string) => {
            setSearchText(value);
        }, 300),
        []
    );

    // Filtering logic
    useEffect(() => {
        let filtered = stores;

        // Default filter based on action
        if (action === 'out') {
            filtered = filtered.filter(s => s.status === 'InStock');
        } else {
            filtered = filtered.filter(s => s.status === 'OutOfStock');
        }

        if (searchText) {
            filtered = filtered.filter(s =>
                s.storeNumber.toLowerCase().includes(searchText.toLowerCase())
            );
        }

        setFilteredStores(filtered);
        setCurrentPage(1);
    }, [stores, searchText, action]);

    const columns: ColumnsType<StoreStatus> = [
        { title: 'Store Number', dataIndex: 'storeNumber', key: 'storeNumber' },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) =>
                status === 'InStock' ? (
                    <Tag color="green">In Stock</Tag>
                ) : (
                    <Tag color="red">Out of Stock</Tag>
                ),
        },
    ];

    return (
        <Drawer
            title={`${itemName} - ${action === 'out' ? 'Mark Out of Stock' : 'Restock'} Stores`}
            width={600}
            onClose={onClose}
            open={visible}
            styles={{
                header: {
                    color: 'white',
                    backgroundColor: 'var(--color-primary)',
                    fontWeight: 350,
                    fontSize: '14px',
                    border: 0,
                    minHeight: '40px',
                },
            }}
            footer={
                <div style={{ textAlign: 'right' }}>
                    <Button
                        type="primary"
                        size="small"
                        onClick={() => onOk(selectedRowKeys, action)}
                        disabled={!selectedRowKeys.length}
                        danger={action === 'out'}
                        style={{ marginRight: 8, fontSize: '12px' }}
                    >
                        {action === 'out' ? 'Mark Out of Stock' : 'Restock'}
                    </Button>
                </div>
            }
        >
            {loading ? (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '300px',
                    }}
                >
                    <Spin size="default" />
                </div>
            ) : (
                <>
                    {/* Search */}
                    <div style={{ display: 'flex', gap: '8px', marginBottom: 16 }}>
                        <Search
                            placeholder="Search stores"
                            allowClear
                            value={searchText}
                            onChange={e => debouncedSearch(e.target.value)}
                            style={{ flex: 1 }}
                        />
                    </div>

                    {/* Counts */}
                    <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
                        <span>Total Stores: {filteredStores.length}</span>
                        <span>Selected: {selectedRowKeys.length}</span>
                    </div>

                    {/* Bulk Select Buttons */}
                    <div style={{ marginBottom: 16 }}>
                        <Button
                            onClick={() => setSelectedRowKeys(filteredStores.map(s => s.storeGuid))}
                            style={{ marginRight: 8 }}
                        >
                            Select All
                        </Button>
                        <Button onClick={() => setSelectedRowKeys([])}>Deselect All</Button>
                    </div>

                    <Table<StoreStatus>
                        rowSelection={{
                            selectedRowKeys,
                            onChange: keys => setSelectedRowKeys(keys as string[]),
                        }}
                        dataSource={filteredStores}
                        columns={columns}
                        rowKey="storeGuid"
                        pagination={{
                            current: currentPage,
                            pageSize,
                            total: filteredStores.length,
                            showSizeChanger: true,
                            pageSizeOptions: ['5', '10', '20', '50'],
                            onChange: (page, size) => {
                                setCurrentPage(page);
                                setPageSize(size);
                            },
                        }}
                    />
                </>
            )}
        </Drawer>
    );
};

export default StoreSelectorDrawer;